import React from 'react';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

interface RichTextContentProps {
    /** HTML aus TipTap-Editor (gespeichert in lesson.content.instruction, html, etc.). */
    html: string;
    /** Optionale zusaetzliche sx-Props (z.B. mb: 3 vor naechster Section). */
    sx?: SxProps<Theme>;
}

/**
 * Rendert eine vom TipTap-Editor (RichTextInput) erzeugte HTML-Ausgabe in
 * den Playern und anderen Anzeige-Kontexten.
 *
 * Hintergrund (Walter-Report 2026-05-26): bootstrap.min.css wird global
 * geladen (src/index.tsx) und ueberschreibt im Reboot ALLE h1-h6-Tags mit
 * eigenen, sehr grossen Schriftgroessen (h1 = 2.5rem usw.). Im Editor sind
 * die Headings durch einen scoped `.ProseMirror`-CSS-Block geschuetzt, im
 * Player landet das HTML aber nackt im DOM und faengt sich die Bootstrap-
 * Sizes ein — Folge: in Lesson 533 (Drag-and-Drop) waren Aufgaben-Texte im
 * Player visuell zertruemmert.
 *
 * Die Typo-Werte hier spiegeln exakt RichTextInput.tsx (.ProseMirror-Block)
 * wider — 2em/1.5em/1.17em/1em sind die HTML5-Browser-Defaults. So sieht
 * der Player aus wie die Editor-Vorschau.
 *
 * Verwendung statt direktem dangerouslySetInnerHTML in DragDropPlayer,
 * InfoTextPlayer, FreeTextPlayer, DocumentLessonPlayer.
 */
const RichTextContent: React.FC<RichTextContentProps> = ({ html, sx }) => {
    // #310-Nachbesserung: TipTap serialisiert Leerzeilen als <p></p>; im Editor
    // rendert ProseMirror dafuer einen trailing <br>. Ohne den kollabiert die
    // Leerzeile im Player auf Hoehe 0 — Absaetze wirken wie ein Textblock.
    const prepared = html.replace(/<p>(\s|&nbsp;)*<\/p>/g, '<p><br></p>');
    return (
        <Box
            sx={[
                {
                    // Editor-konforme Heading-Skala — bricht den Bootstrap-Reboot.
                    '& h1': { fontSize: '2em', fontWeight: 700, margin: '0.67em 0' },
                    '& h2': { fontSize: '1.5em', fontWeight: 700, margin: '0.75em 0' },
                    '& h3': { fontSize: '1.17em', fontWeight: 600, margin: '0.83em 0' },
                    '& h4': { fontSize: '1em', fontWeight: 600, margin: '1em 0' },
                    '& h5': { fontSize: '0.83em', fontWeight: 600, margin: '1.5em 0' },
                    '& h6': { fontSize: '0.75em', fontWeight: 600, margin: '1.67em 0' },
                    '& p': { marginBottom: 1, marginTop: 0 },
                    '& ul, & ol': { paddingLeft: 3, marginBottom: 1 },
                    '& li': { marginBottom: 0.5 },
                    // Issue #141 follow-up: List-Marker erbt Bold/Italic/FontSize vom <li>.
                    '& li::marker': {
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        fontStyle: 'inherit',
                    },
                    '& blockquote': {
                        borderLeft: '4px solid #e0e0e0',
                        paddingLeft: 2,
                        fontStyle: 'italic',
                        marginLeft: 0,
                        marginBottom: 2,
                        color: 'text.secondary',
                    },
                    '& pre': {
                        backgroundColor: '#f5f5f5',
                        padding: 2,
                        borderRadius: 1,
                        overflowX: 'auto',
                        marginBottom: 2,
                    },
                    '& code': {
                        backgroundColor: '#f5f5f5',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '0.9em',
                    },
                    '& a': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                        '&:hover': { textDecoration: 'none' },
                    },
                    '& img': { maxWidth: '100%', height: 'auto' },
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            dangerouslySetInnerHTML={{ __html: prepared }}
        />
    );
};

export default RichTextContent;
